import { Stack } from "@mui/material";
import { SBBox, SBLoading } from "modules/Commons";
import { TimePeriod } from "domain/DatePickerConfig/domain/enums/TimePeriod";
import { IHomeStatistics } from "domain/Utils/models/IHomeStatistics";
import SBForecastPeriodFilter from "modules/Forecast/Components/SBForecastPeriodFilter";
import SBForecastTitleLine from "modules/Forecast/Components/SBForecastTitleLine";
import { useForecastExporter } from "modules/Forecast/Hooks/useForecastExporter";
import { forwardRef, useMemo, useState } from "react";
import ForecastSummarizationChart from "views/PurchaseViews/PurchaseAlertsView/HomeAlertCard/Summarizations/ForecastSummarization/ForecastSummarizationChart";
import SBForecastEvaluationSection from "../SBForecastEvaluationSection";
import ProductModelTimeSeriesTable from "views/ProductModelAnalyserView/components/ProductModelTimeSeriesTable";

export default ({
	statistics,
	breakLine,
	isCost,
	isOnModal = false,
	hideBenchmark = false,
	primaryLabel,
	benchmarkLabel,
	showTimeSeriesTable = false,
	hideChartAndKPIs = false,
	loading = false,
	children,
	noBorder = false,
	noTimeSeriesPadding = false,
}: SBForecastEvaluationProps) => {
	const { asCSV } = useForecastExporter();
	const [timePeriod, setTimePeriod] = useState(TimePeriod.MonthlyUTC);

	const demand = useMemo(
		() =>
			timePeriod === TimePeriod.DailyUTC
				? statistics?.evaluation?.demand
				: statistics?.evaluation?.monthlyDemand,
		[timePeriod, statistics]
	);

	const benchmarkDemand = useMemo(
		() =>
			timePeriod === TimePeriod.DailyUTC
				? statistics?.benchmarkEvaluation?.demand
				: statistics?.benchmarkEvaluation?.monthlyDemand,
		[timePeriod, statistics]
	);

	const exportCSV = () => asCSV({ demand, benchmarkDemand });

	return (
		<Stack spacing={2}>
			{!hideChartAndKPIs && (
				<SBBox bgcolor="white" borderRadius="8px" p={3} border={noBorder ? "none" : "1px solid #E0E0E0"}>
					<SBForecastTitleLine
						exportCSV={exportCSV}
					/>
					<ForecastSummarizationChart
						dataPoints={demand ?? []}
						benchmarkDataPoints={benchmarkDemand ?? []}
						hideBenchmark={hideBenchmark}
						primaryLabel={primaryLabel}
						benchmarkLabel={benchmarkLabel}
						dateFilter={({ onUpdate }) =>
							<SBForecastPeriodFilter
								setTimePeriod={setTimePeriod}
								timePeriod={timePeriod}
								onUpdate={onUpdate}
							/>
						}
					/>
					<SBForecastEvaluationSection
						breakLine={breakLine}
						statistics={statistics}
						isMonthly={timePeriod == TimePeriod.MonthlyUTC}
						isCost={isCost}
						isOnModal={isOnModal}
						hideBenchmark={hideBenchmark}
					/>
				</SBBox>
			)}
			{children}
			{showTimeSeriesTable && demand && (
				<SBLoading loading={loading} limited fullWidth>
					<SBBox bgcolor="white" borderRadius="8px" p={3} pt={noTimeSeriesPadding ? 0 : 3}>
						<ProductModelTimeSeriesTable
							demand={demand as any}
							benchmarkDemand={benchmarkDemand as any}
							primaryLabel={primaryLabel}
							benchmarkLabel={benchmarkLabel}
						/>
					</SBBox>
				</SBLoading>
			)}
		</Stack>
	);
};

interface SBForecastEvaluationProps {
	statistics: IHomeStatistics;
	breakLine?: boolean;
	isCost: boolean;
	isOnModal?: boolean;
    hideBenchmark?: boolean;
    primaryLabel?: string;
    benchmarkLabel?: string;
	showTimeSeriesTable?: boolean;
	hideChartAndKPIs?: boolean;
	loading?: boolean;
	children?: React.ReactNode;
	noBorder?: boolean;
	noTimeSeriesPadding?: boolean;
}
